import React from 'react';
import { Link } from 'react-router';
import { useGetProfileSettingsQuery } from 'store/services/profile/profileApi';
import { useWantListFetchPaginatedQuery } from 'store/services/collectionList/wantList/wantListApi';
import { useHaveListFetchPaginatedQuery } from 'store/services/collectionList/haveList/haveListApi';

export interface ReadinessSamples {
  haveScryfallId: string | null;
  wantScryfallId: string | null;
}

/**
 * All three facts come from existing endpoints with server-computed totals
 * (ruling: Backend Contract item 3 is unnecessary; never fetch a list to
 * count client-side): the profile settings flag, and the paginated list
 * responses' SQL-computed count fields — the have query filtered server-side
 * with forTrade=false. The page-1 rows double as swap-diagram samples.
 */
export const useReadinessData = () => {
  const settings = useGetProfileSettingsQuery();
  const wantPage = useWantListFetchPaginatedQuery({ page: 1 });
  const notForTradePage = useHaveListFetchPaginatedQuery({ page: 1, forTrade: false });

  const isLoading = settings.isLoading || wantPage.isLoading || notForTradePage.isLoading;
  const hasError = !!settings.error || !!wantPage.error || !!notForTradePage.error;

  return {
    isLoading,
    hasError,
    hasPublicHaveList: settings.data?.showHaveListOnProfile ?? null,
    wantListCount: wantPage.data?.totalCards ?? null,
    // totalRecords is scoped to the forTrade filter; totalCards is NOT (it is
    // the global quantity sum — verified live 2026-07-28, flagged to backend).
    notForTradeCount: notForTradePage.data?.totalRecords ?? null,
    samples: {
      haveScryfallId: notForTradePage.data?.haveList?.[0]?.card?.scryfallID ?? null,
      wantScryfallId: wantPage.data?.wantList?.[0]?.card?.scryfallID ?? null,
    } as ReadinessSamples,
  };
};

interface ReadinessChecklistProps {
  hasPublicHaveList: boolean;
  wantListCount: number;
  notForTradeCount: number;
}

/**
 * Below this many want-list cards the row nudges rather than passes: a thin
 * want list gives traders little to build offers around. (Value per Eric's
 * review, 2026-07-28.)
 */
export const WANT_LIST_HEALTHY_MIN = 25;

interface RowProps {
  ok: boolean;
  title: string;
  /** The consequence, not the instruction */
  reason: string;
  linkTo: string;
  linkLabel: string;
  /** Prompts get a trailing arrow; neutral destinations don't */
  isPrompt?: boolean;
}

const Row: React.FC<RowProps> = ({ ok, title, reason, linkTo, linkLabel, isPrompt = false }) => (
  <li className="offers-zero-ready-row">
    <span
      className={`offers-zero-ready-marker ${ok ? 'offers-zero-ready-marker--ok' : 'offers-zero-ready-marker--attention'}`}
      aria-hidden="true"
    >
      {ok ? '✓' : '!'}
    </span>
    <div className="offers-zero-ready-text">
      <div className="offers-zero-ready-title">{title}</div>
      <div className="offers-zero-ready-reason">{reason}</div>
    </div>
    <Link className="offers-zero-ready-link" to={linkTo}>
      {linkLabel}
      {isPrompt ? ' →' : ''}
    </Link>
  </li>
);

const ReadinessChecklist: React.FC<ReadinessChecklistProps> = ({
  hasPublicHaveList,
  wantListCount,
  notForTradeCount,
}) => (
  <section className="offers-zero-ready" aria-label="Get offers coming to you">
    <h2 className="offers-zero-block-heading">Get offers coming to you</h2>
    <p className="offers-zero-block-subhead">
      Traders can only build you an offer if they can see what you have and what you&apos;re
      after.
    </p>
    <ul className="offers-zero-ready-rows">
      <Row
        ok={hasPublicHaveList}
        title={hasPublicHaveList ? 'Your have list is public' : 'Your have list is private'}
        reason={
          hasPublicHaveList
            ? 'Traders can see what you own and build offers around it.'
            : "Traders can't see what you own, so no offer can start from your collection."
        }
        linkTo="/settings"
        linkLabel={hasPublicHaveList ? 'Privacy settings' : 'Make it public'}
        isPrompt={!hasPublicHaveList}
      />
      <Row
        ok={wantListCount >= WANT_LIST_HEALTHY_MIN}
        title={
          wantListCount > 0
            ? `${wantListCount.toLocaleString()} ${wantListCount === 1 ? 'card' : 'cards'} on your want list`
            : 'Your want list is empty'
        }
        reason={
          wantListCount >= WANT_LIST_HEALTHY_MIN
            ? 'These tell traders which of their cards you would accept.'
            : wantListCount > 0
              ? "More wants mean more possible matches. Cards not listed can't be asked for."
              : "Cards not listed can't be asked for, so traders won't know what you want."
        }
        linkTo="/my-cards"
        linkLabel={wantListCount > 0 ? 'Add more wants' : 'Add wants'}
        isPrompt
      />
      <Row
        ok={notForTradeCount === 0}
        title={
          notForTradeCount > 0
            ? `${notForTradeCount.toLocaleString()} owned ${notForTradeCount === 1 ? 'card' : 'cards'} not marked for trade`
            : 'Everything you own is marked for trade'
        }
        reason={
          notForTradeCount > 0
            ? "Cards not marked for trade can't be offered, so they never appear in a trade."
            : 'Your whole collection can appear in offers.'
        }
        linkTo="/my-cards/have-list"
        linkLabel={notForTradeCount > 0 ? 'Mark cards for trade' : 'View collection'}
        isPrompt={notForTradeCount > 0}
      />
    </ul>
  </section>
);

export default ReadinessChecklist;
